import React, { useEffect, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	getTrialStandardDifferentiatorsCohort,
	manuallyLogTrialStandardDifferentiatorsExposure,
} from '@confluence/onboarding-helpers/entry-points/flags/experiment-edition-awareness-flags';
import type { Cohorts } from '@confluence/onboarding-helpers/entry-points/constants/cohorts';
import { useCoordinationClient } from '@confluence/engagement-provider';
import {
	confluenceLocalStorageInstance as localStorage,
	PERSISTED_KEYS_ON_SERVER,
} from '@confluence/storage-manager';
import {
	ExperienceFailure,
	ExperienceStart,
	STANDARD_DIFFERENTIATORS_ONBOARDING_EXPERIENCE,
} from '@confluence/experience-tracker';

import { DifferentiatorsModal } from './ui/differentiators-modal';
import { useStandardDifferentiatorsExperiment } from './hooks/useStandardDifferentiatorsExperiment';
import { useHasBillingInformation } from './hooks/useHasBillingInformation';

const EXPERIMENT_KEY = 'confluence_standard_differentiators';
const EXTERNAL_EP_KEY = 'confluence-standard-differentiators';

export const StandardDifferentiatorsOnboarding = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const {
		isQualified,
		isVariation,
		error: trialError,
		loading: trialLoading,
	} = useStandardDifferentiatorsExperiment();

	const {
		hasBillingInformation,
		error: hasBillingError,
		loading: loadingBillingInfo,
	} = useHasBillingInformation(!isQualified);

	const error = trialError || hasBillingError;
	const loading = trialLoading || loadingBillingInfo;

	const [startExperiment, setStartExperiment] = useState<boolean>(false);

	const coordinationClient = useCoordinationClient();

	const fireExposedEvent = (flagKey: string, value: Cohorts) => {
		return {
			type: 'sendTrackEvent',
			data: {
				source: 'standard-differentiators',
				action: 'exposed',
				actionSubject: 'feature',
				actionSubjectId: 'standardDifferentiators',
				attributes: {
					flagKey,
					value,
					edition: 'STANDARD',
					growthInitiative: 'luna',
				},
			},
		};
	};

	useEffect(() => {
		if (isQualified && !hasBillingInformation && !loading) {
			void init();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isQualified, hasBillingInformation, loading]);

	const init = async () => {
		const hasMessageStarted = await coordinationClient.start(EXTERNAL_EP_KEY);
		if (hasMessageStarted) {
			localStorage.setItem(
				PERSISTED_KEYS_ON_SERVER.PERSISTED_STANDARD_DIFFERENTIATORS_HAS_STARTED,
				'true',
			);
			setStartExperiment(true);
			manuallyLogTrialStandardDifferentiatorsExposure();

			createAnalyticsEvent(
				fireExposedEvent(EXPERIMENT_KEY, getTrialStandardDifferentiatorsCohort()),
			).fire();
		}

		const hasStartedAndReload: boolean = localStorage.getItemAsBoolean(
			PERSISTED_KEYS_ON_SERVER.PERSISTED_STANDARD_DIFFERENTIATORS_HAS_STARTED,
		);

		if (!hasMessageStarted && hasStartedAndReload) {
			setStartExperiment(true);
		}
	};

	const handleOnClose = () => {
		localStorage.setItem(
			PERSISTED_KEYS_ON_SERVER.PERSISTED_STANDARD_DIFFERENTIATORS_HAS_STARTED,
			'false',
		);
		localStorage.setItem(
			PERSISTED_KEYS_ON_SERVER.PERSISTED_STANDARD_DIFFERENTIATORS_HAS_SEEN,
			'true',
		);
		void coordinationClient.stop(EXTERNAL_EP_KEY);
	};

	if (startExperiment && isVariation) {
		return (
			<>
				<ExperienceStart name={STANDARD_DIFFERENTIATORS_ONBOARDING_EXPERIENCE} />
				{error ? (
					<ExperienceFailure name={STANDARD_DIFFERENTIATORS_ONBOARDING_EXPERIENCE} error={error} />
				) : (
					<DifferentiatorsModal
						touchpointId="trial-onboarding"
						onClose={handleOnClose}
						hasBillingInfo={hasBillingInformation}
					/>
				)}
			</>
		);
	}

	return null;
};
